import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, MessageCircle, Download, Link2, RotateCcw, Settings, X, Check } from 'lucide-react';
import { trackEvent } from '../utils/posthog';
import { triggerSubtleConfetti } from '../utils/confetti';
interface QuickAction {
  id: string;
  icon: ComponentType<{
    className?: string;
  }>;
  label: string;
  onClick: () => void;
  color?: string;
}
interface SpotlightWidgetProps {
  actions: QuickAction[];
  onRestartTour: () => void;
  onToggleTour: (enabled: boolean) => void;
  tourEnabled: boolean;
}
export function SpotlightWidget({
  actions,
  onRestartTour,
  onToggleTour,
  tourEnabled
}: SpotlightWidgetProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const widgetRef = useRef<HTMLDivElement>(null);
  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (widgetRef.current && !widgetRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
        setShowSettings(false);
      }
    };
    if (isExpanded || showSettings) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded, showSettings]);
  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
    setShowSettings(false);
    if (!isExpanded) {
      trackEvent('spotlight_widget_opened');
    }
  };
  const handleActionClick = (action: QuickAction) => {
    triggerSubtleConfetti(widgetRef.current!);
    trackEvent('spotlight_widget_action', {
      action_id: action.id,
      action_label: action.label
    });
    action.onClick();
    setIsExpanded(false);
  };
  const handleRestartTour = () => {
    triggerSubtleConfetti(widgetRef.current!);
    trackEvent('spotlight_tour_restarted', {
      source: 'widget'
    });
    onRestartTour();
    setIsExpanded(false);
    setShowSettings(false);
  };
  const handleToggleSettings = () => {
    setShowSettings(!showSettings);
  };
  const handleTourToggle = () => {
    const newState = !tourEnabled;
    onToggleTour(newState);
    trackEvent('spotlight_tour_toggled', {
      enabled: newState,
      source: 'widget'
    });
  };
  return <div ref={widgetRef} className="fixed bottom-6 right-6 z-[9997]" data-id="element-813">
      <AnimatePresence data-id="element-814">
        {/* Expanded Menu */}
        {isExpanded && !showSettings && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} transition={{
        duration: 0.2,
        type: 'spring',
        stiffness: 300
      }} className="absolute bottom-16 right-0 w-64 bg-white rounded-xl shadow-lg border border-sage-mint/20 overflow-hidden" data-id="element-815">
            {/* Header */}
            <div className="bg-gradient-to-r from-sage-mint/10 to-coral-pink/10 px-4 py-3 border-b border-grey-400/10" data-id="element-816">
              <div className="flex items-center justify-between" data-id="element-817">
                <h3 className="text-sm font-medium text-charcoal" data-id="element-818">
                  Quick Actions
                </h3>
                <button onClick={() => setIsExpanded(false)} className="text-grey-400 hover:text-grey-600 transition-colors" data-id="element-819">
                  <X className="h-4 w-4" data-id="element-820" />
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="p-2" data-id="element-821">
              {actions.map((action, index) => <motion.button key={action.id} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: index * 0.05
          }} onClick={() => handleActionClick(action)} className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-sage-mint/10 transition-colors group" data-id="element-822">
                  <div className={`w-8 h-8 rounded-full ${action.color || 'bg-sage-mint/20'} flex items-center justify-center group-hover:scale-110 transition-transform`} data-id="element-823">
                    <action.icon className="h-4 w-4 text-sage-mint" data-id="element-824" />
                  </div>
                  <span className="text-sm font-medium text-charcoal" data-id="element-825">
                    {action.label}
                  </span>
                </motion.button>)}
            </div>

            {/* Divider */}
            <div className="border-t border-grey-400/10 mx-2" data-id="element-826" />

            {/* Tour Controls */}
            <div className="p-2" data-id="element-827">
              <button onClick={handleRestartTour} className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-sage-mint/10 transition-colors group" data-id="element-828">
                <div className="w-8 h-8 rounded-full bg-sage-mint/20 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-829">
                  <RotateCcw className="h-4 w-4 text-sage-mint" data-id="element-830" />
                </div>
                <span className="text-sm font-medium text-charcoal" data-id="element-831">
                  Restart Tour
                </span>
              </button>
              <button onClick={handleToggleSettings} className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-sage-mint/10 transition-colors group" data-id="element-832">
                <div className="w-8 h-8 rounded-full bg-sage-mint/20 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-833">
                  <Settings className="h-4 w-4 text-sage-mint" data-id="element-834" />
                </div>
                <span className="text-sm font-medium text-charcoal" data-id="element-835">
                  Tour Settings
                </span>
              </button>
            </div>
          </motion.div>}

        {/* Settings Panel */}
        {showSettings && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} transition={{
        duration: 0.2,
        type: 'spring',
        stiffness: 300
      }} className="absolute bottom-16 right-0 w-64 bg-white rounded-xl shadow-lg border border-sage-mint/20 overflow-hidden" data-id="element-836">
            {/* Header */}
            <div className="bg-gradient-to-r from-sage-mint/10 to-coral-pink/10 px-4 py-3 border-b border-grey-400/10" data-id="element-837">
              <div className="flex items-center justify-between" data-id="element-838">
                <h3 className="text-sm font-medium text-charcoal" data-id="element-839">
                  Tour Settings
                </h3>
                <button onClick={() => setShowSettings(false)} className="text-grey-400 hover:text-grey-600 transition-colors" data-id="element-840">
                  <X className="h-4 w-4" data-id="element-841" />
                </button>
              </div>
            </div>

            {/* Settings */}
            <div className="p-4" data-id="element-842">
              <div className="flex items-center justify-between" data-id="element-843">
                <div data-id="element-844">
                  <p className="text-sm font-medium text-charcoal" data-id="element-845">
                    Show tour for new visitors
                  </p>
                  <p className="text-xs text-grey-600 mt-0.5" data-id="element-846">
                    Automatically guide first-time users
                  </p>
                </div>
                <button onClick={handleTourToggle} className={`relative w-11 h-6 rounded-full transition-colors ${tourEnabled ? 'bg-sage-mint' : 'bg-grey-400/30'}`} data-id="element-847">
                  <motion.div animate={{
                x: tourEnabled ? 20 : 2
              }} transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30
              }} className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm" data-id="element-848" />
                </button>
              </div>

              <div className="mt-4 p-3 bg-sage-mint/5 rounded-lg" data-id="element-849">
                <p className="text-xs text-grey-600 leading-relaxed" data-id="element-850">
                  The tour will automatically show for new visitors and users
                  who haven't visited in 7+ days. You can always restart it
                  manually.
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Main Widget Button */}
      <motion.button onClick={handleToggleExpand} whileHover={{
      scale: 1.1
    }} whileTap={{
      scale: 0.95
    }} className="w-14 h-14 rounded-full bg-gradient-to-r from-sage-mint to-sage-mint/90 text-white shadow-lg hover:shadow-glow-mint transition-shadow flex items-center justify-center relative overflow-hidden group" data-id="element-851">
        {/* Animated background gradient */}
        <motion.div animate={{
        rotate: [0, 360]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'linear'
      }} className="absolute inset-0 bg-gradient-to-r from-sage-mint via-coral-pink/30 to-sage-mint opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-852" />

        {/* Icon */}
        <AnimatePresence mode="wait" data-id="element-853">
          {isExpanded ? <motion.div key="close" initial={{
          rotate: -90,
          opacity: 0
        }} animate={{
          rotate: 0,
          opacity: 1
        }} exit={{
          rotate: 90,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} data-id="element-854">
              <X className="h-6 w-6 relative z-10" data-id="element-855" />
            </motion.div> : <motion.div key="sparkles" initial={{
          rotate: 90,
          opacity: 0
        }} animate={{
          rotate: 0,
          opacity: 1
        }} exit={{
          rotate: -90,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} data-id="element-856">
              <Sparkles className="h-6 w-6 relative z-10" data-id="element-857" />
            </motion.div>}
        </AnimatePresence>

        {/* Pulse animation when not expanded */}
        {!isExpanded && <motion.div animate={{
        scale: [1, 1.2, 1],
        opacity: [0.5, 0, 0.5]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute inset-0 rounded-full bg-sage-mint" data-id="element-858" />}
      </motion.button>
    </div>;
}
export default SpotlightWidget;